import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { HiFilter, HiLocationMarker, HiX } from 'react-icons/hi';
import { IoIosSearch } from 'react-icons/io';
import InfiniteScroll from 'react-infinite-scroller';

import {
  searchShopsByText,
  countShopsByText,
} from '@/api/fetch/marketplace-api';
import {
  SearchUsersByTitleRequest,
  PaginationParams,
  MatchParams,
} from '@/api/gen-protos/discovery_pb';
import { LIMIT } from '@/lib/constants';
import { getDelegationsOfGovernorate, governorates } from '@/lib/helpers';
import { cleanObject } from '@/lib/utils';
import Layout from '@/modules/core/components/Layout';
import { mapUserResponseToIUserData } from '@/modules/core/mappings';
import { IUserData } from '@/modules/core/types';
import InlistShop from '@/tayara-kit/banners/InListShop';
import CriteriaSelect from '@/tayara-kit/CriteriaSelect';
import PublisherCard from '@/tayara-kit/PublisherCard';
import { SelectOption } from '@/tayara-kit/Select';

// Constants
const INITIAL_DISPLAY_COUNT = 20;
const LOAD_MORE_COUNT = 20;
const MAX_SHOPS_TO_LOAD = 1000;
const NEUF_SUBCATEGORY_ID = '66c34239d16d7f3b0c2ec56e';

// Types
interface ShopsProps {
  shops: IUserData[];
  countShops: number;
}

interface FilterState {
  governorate: string;
  delegation: string;
  title: string;
}

interface LoadingState {
  isLoading: boolean;
  hasLoadedAll: boolean;
}

// Custom hooks
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    updateWindowSize();
    window.addEventListener('resize', updateWindowSize);
    return () => window.removeEventListener('resize', updateWindowSize);
  }, []);

  return windowSize;
};

const useFilterState = () => {
  const [selectedGovernorate, setSelectedGovernorate] = useState<string>('');
  const [selectedDelegation, setSelectedDelegation] = useState<string>('');
  const [searchTitle, setSearchTitle] = useState<string>('');
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    governorate: '',
    delegation: '',
    title: '',
  });

  const availableDelegations = useMemo(() => {
    if (!selectedGovernorate) return [];
    return getDelegationsOfGovernorate(selectedGovernorate);
  }, [selectedGovernorate]);

  // Reset delegation when governorate changes
  useEffect(() => {
    if (selectedGovernorate !== activeFilters.governorate) {
      setSelectedDelegation('');
    }
  }, [selectedGovernorate, activeFilters.governorate]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        activeFilters.governorate ||
          activeFilters.delegation ||
          activeFilters.title
      ),
    [activeFilters]
  );

  return {
    selectedGovernorate,
    setSelectedGovernorate,
    selectedDelegation,
    setSelectedDelegation,
    searchTitle,
    setSearchTitle,
    activeFilters,
    setActiveFilters,
    availableDelegations,
    hasActiveFilters,
  };
};

// Utility functions
const getAddressFields = (shop: IUserData): string[] =>
  [
    shop.address,
    shop.location,
    shop.city,
    shop.governorate,
    shop.region,
    shop.area,
    shop.fullAddress,
    shop.delegation,
    shop.district,
    shop.neighborhood,
    shop.street,
  ].filter(Boolean);

const getSearchableFields = (shop: IUserData): string[] =>
  [
    shop.fullname,
    shop.shopDescription,
    shop.businessName,
    shop.companyName,
  ].filter(Boolean);

const normalizeText = (text: string): string => text.toLowerCase().trim();

// Main Component
const Neuf = ({ shops: initialShops }: ShopsProps) => {
  // State management
  const [allShops, setAllShops] = useState<IUserData[]>(initialShops);
  const [filteredShops, setFilteredShops] = useState<IUserData[]>(initialShops);
  const [displayedShops, setDisplayedShops] = useState<IUserData[]>(
    initialShops.slice(0, INITIAL_DISPLAY_COUNT)
  );
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false,
    hasLoadedAll: false,
  });

  // Custom hooks
  const windowSize = useWindowSize();
  const {
    selectedGovernorate,
    setSelectedGovernorate,
    selectedDelegation,
    setSelectedDelegation,
    searchTitle,
    setSearchTitle,
    activeFilters,
    setActiveFilters,
    availableDelegations,
    hasActiveFilters,
  } = useFilterState();

  // Memoized values
  const GreenIndexes = useMemo(
    () => [displayedShops?.length],
    [displayedShops?.length]
  );

  // API functions
  const loadAllShops = useCallback(async () => {
    if (loadingState.hasLoadedAll || loadingState.isLoading) return;

    setLoadingState((prev) => ({ ...prev, isLoading: true }));

    try {
      const paginationParams = new PaginationParams();
      paginationParams.setLimit(MAX_SHOPS_TO_LOAD);
      paginationParams.setSkip(0);

      const matchParams = new MatchParams();
      matchParams.setPaginationparams(paginationParams);

      const searchShopRequest = new SearchUsersByTitleRequest();
      searchShopRequest.setMatchparams(matchParams);
      searchShopRequest.setSubcategoryid(NEUF_SUBCATEGORY_ID);

      const allShopsResponse = await searchShopsByText('', searchShopRequest);
      const mappedShops = allShopsResponse.map(mapUserResponseToIUserData);

      console.log(`✅ Loaded ${mappedShops.length} total shops for filtering`);

      setAllShops(mappedShops);
      setLoadingState({ isLoading: false, hasLoadedAll: true });

      // Update filtered and displayed shops if no filters are active
      if (
        !activeFilters.governorate &&
        !activeFilters.delegation &&
        !activeFilters.title
      ) {
        setFilteredShops(mappedShops);
        setDisplayedShops(mappedShops.slice(0, INITIAL_DISPLAY_COUNT));
      }
    } catch (error) {
      console.error('❌ Failed to load all shops:', error);
      setLoadingState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [loadingState.hasLoadedAll, loadingState.isLoading, activeFilters]);

  // Filtering logic
  const applyClientSideFilters = useCallback(
    (shopsToFilter: IUserData[], filters: FilterState): IUserData[] => {
      console.log('🔍 Applying filters:', filters);

      let filtered = [...shopsToFilter];

      // Title/search filter
      if (filters.title?.trim()) {
        const searchTerm = normalizeText(filters.title);
        filtered = filtered.filter((shop) => {
          const searchableText = getSearchableFields(shop)
            .join(' ')
            .toLowerCase();
          return searchableText.includes(searchTerm);
        });
        console.log(`📝 After title filter: ${filtered.length} shops`);
      }

      // Governorate filter
      if (filters.governorate) {
        const govName = governorates.find(
          (g) => g.id === filters.governorate
        )?.name;
        if (govName) {
          const govNameLower = normalizeText(govName);
          filtered = filtered.filter((shop) => {
            const addressText = getAddressFields(shop).join(' ').toLowerCase();
            return addressText.includes(govNameLower);
          });
          console.log(
            `🏛️ After governorate filter (${govName}): ${filtered.length} shops`
          );
        }
      }

      // Delegation filter
      if (filters.delegation) {
        const delName = availableDelegations.find(
          (d) => d.id === filters.delegation
        )?.name;
        if (delName) {
          const delNameLower = normalizeText(delName);
          filtered = filtered.filter((shop) => {
            const addressText = getAddressFields(shop).join(' ').toLowerCase();
            return addressText.includes(delNameLower);
          });
          console.log(
            `📍 After delegation filter (${delName}): ${filtered.length} shops`
          );
        }
      }

      console.log(`✅ Final filtered result: ${filtered.length} shops`);
      return filtered;
    },
    [availableDelegations]
  );

  // Event handlers
  const handleApplyFilters = useCallback(() => {
    if (!loadingState.hasLoadedAll) {
      loadAllShops();
      return;
    }

    const newFilters: FilterState = {
      governorate: selectedGovernorate,
      delegation: selectedDelegation,
      title: searchTitle,
    };

    setActiveFilters(newFilters);

    const filtered = applyClientSideFilters(allShops, newFilters);
    setFilteredShops(filtered);
    setDisplayedShops(filtered.slice(0, INITIAL_DISPLAY_COUNT));
  }, [
    loadingState.hasLoadedAll,
    selectedGovernorate,
    selectedDelegation,
    searchTitle,
    allShops,
    applyClientSideFilters,
    loadAllShops,
  ]);

  const handleClearFilters = useCallback(() => {
    console.log('🗑️ Clearing all filters');
    setSelectedGovernorate('');
    setSelectedDelegation('');
    setSearchTitle('');
    setActiveFilters({ governorate: '', delegation: '', title: '' });
    setFilteredShops(allShops);
    setDisplayedShops(allShops.slice(0, INITIAL_DISPLAY_COUNT));
  }, [allShops]);

  const handleClearSingleFilter = useCallback(
    (filterType: keyof FilterState) => {
      switch (filterType) {
        case 'governorate':
          setSelectedGovernorate('');
          break;
        case 'delegation':
          setSelectedDelegation('');
          break;
        case 'title':
          setSearchTitle('');
          break;
        default:
          console.warn(`Unknown filter type: ${filterType}`);
          return;
      }

      // Apply filters after clearing
      setTimeout(handleApplyFilters, 0);
    },
    [handleApplyFilters]
  );

  const loadMore = useCallback(() => {
    if (loadingState.isLoading) return;

    const currentCount = displayedShops.length;
    const nextBatch = filteredShops.slice(
      currentCount,
      currentCount + LOAD_MORE_COUNT
    );

    if (nextBatch.length > 0) {
      setDisplayedShops((prev) => [...prev, ...nextBatch]);
    }
  }, [displayedShops.length, filteredShops, loadingState.isLoading]);

  // Effects
  useEffect(() => {
    loadAllShops();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Render helpers
  const renderFilterButton = useCallback(
    (filterType: keyof FilterState, label: string) => (
      <span
        key={filterType}
        className="inline-flex items-center bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm"
      >
        {label}
        <button
          onClick={() => handleClearSingleFilter(filterType)}
          className="ml-2 text-blue-600 hover:text-blue-800 transition-colors"
          aria-label={`Remove ${filterType} filter`}
          type="button"
        >
          <HiX size={14} />
        </button>
      </span>
    ),
    [handleClearSingleFilter]
  );

  const renderEmptyState = useCallback(
    () => (
      <div className="w-full text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">🔍</div>
        <p className="text-gray-500 text-xl mb-2">Aucun projet trouvé</p>
        <p className="text-gray-400 text-sm mb-4">
          Aucun projet ne correspond à vos critères de recherche.
        </p>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            type="button"
          >
            Voir tous les projets
          </button>
        )}
      </div>
    ),
    [hasActiveFilters, handleClearFilters]
  );

  const renderLoadingState = () => (
    <div className="w-full text-center py-12">
      <div className="text-gray-400 text-4xl mb-4">⏳</div>
      <p className="text-gray-500">Chargement des projets...</p>
    </div>
  );

  // Transform data for select options
  const governorateOptions = useMemo(
    () => [
      { value: '', label: 'Tous les gouvernorats' },
      ...governorates
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(
          (gov): SelectOption => ({
            value: gov.id,
            label: gov.name,
          })
        ),
    ],
    []
  );

  const delegationOptions = useMemo(
    () => [
      { value: '', label: 'Toutes les délégations' },
      ...availableDelegations
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(
          (del): SelectOption => ({
            value: del.id,
            label: del.name,
          })
        ),
    ],
    [availableDelegations]
  );

  return (
    <div className="min-h-screen py-8">
      {/* Hero Section */}
      <div className="relative mb-8">
        <img
          src="https://storage.googleapis.com/caravel-images/home.jpg"
          alt="Cover"
          className="w-full h-96 object-cover rounded-2xl"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/20 flex flex-col items-center justify-center rounded-2xl">
          <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-black leading-none mb-4">
            ImmoNeuf
          </h1>
          <h2 className="text-white text-xl md:text-2xl lg:text-4xl font-bold text-center">
            Tout L&apos;immobilier Neuf en Tunisie en 1 Clic !
          </h2>
        </div>
      </div>

      {/* Header and Filters */}
      <div className="flex  flex-wrap xl:flex-nowrap  justify-between gap-2 xl:gap-20 items-center w-full mb-10 xl:my-10">
        {/* Title Section */}
        <div className="flex w-full lg:w-fit flex-col justify-center h-full">
          <h1 className="text-2xl font-bold text-gray-700 text-center lg:text-start">
            Visiter un projet
          </h1>
          <p className="text-sm truncate lg:text-base font-bold text-blue-600 text-center lg:text-start">
            {displayedShops.length} projets affichés sur {filteredShops.length}{' '}
            trouvés
            {hasActiveFilters && ` (${allShops.length} total)`}
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col lg:flex-row justify-end items-center gap-3 w-full">
          {/* Search Input */}
          <div className="relative w-[90%] md:w-full my-1 h-fit">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoIosSearch className="h-6 w-6 text-neutral-400" />
            </div>
            <input
              type="text"
              className="w-full h-12 pl-12 pr-4 placeholder:text-neutral-400 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all"
              placeholder="Rechercher un projet..."
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
            />
            <div className="lg:flex hidden absolute inset-y-0 right-0 items-center pr-3">
              <span className="text-xs text-white  bg-neutral-600 px-2 py-1 rounded-md">
                Entrée ↵
              </span>
            </div>
          </div>
          <div className="w-full flex flex-wrap md:flex-nowrap gap-2">
            {/* Governorate Select */}
            <CriteriaSelect
              icon={HiLocationMarker}
              onChange={(e) => setSelectedGovernorate(e.target.value)}
              displayLabel="Gouvernorat"
              value={selectedGovernorate}
              options={governorateOptions}
              className="w-fit"
            />

            {/* Delegation Select */}
            <CriteriaSelect
              onChange={(e) => setSelectedDelegation(e.target.value)}
              displayLabel="Délégation"
              value={selectedDelegation}
              disabled={availableDelegations.length === 0}
              options={delegationOptions}
              className="w-fit"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex w-full lg:w-fit gap-2">
            <button
              className="flex w-full items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-md h-11 px-6 disabled:opacity-50 text-white font-medium transition-colors"
              onClick={handleApplyFilters}
              disabled={loadingState.isLoading}
              type="button"
            >
              <HiFilter size={18} className="mr-2" />
              {loadingState.isLoading ? 'Filtrage...' : 'Filtrer'}
            </button>

            {hasActiveFilters && (
              <button
                className="flex items-center justify-center bg-neutral-500 hover:bg-gray-600 rounded-md h-11 px-4 text-white transition-colors"
                onClick={handleClearFilters}
                type="button"
              >
                <HiX size={18} className="mr-1" />
                Effacer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-blue-800">Filtres actifs:</span>
            {activeFilters.governorate &&
              renderFilterButton(
                'governorate',
                governorates.find((g) => g.id === activeFilters.governorate)
                  ?.name || ''
              )}
            {activeFilters.delegation &&
              renderFilterButton(
                'delegation',
                availableDelegations.find(
                  (d) => d.id === activeFilters.delegation
                )?.name || ''
              )}
            {activeFilters.title &&
              renderFilterButton('title', `"${activeFilters.title}"`)}
          </div>
        </div>
      )}

      {/* Results Grid */}
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMore}
        hasMore={
          displayedShops.length < filteredShops.length &&
          !loadingState.isLoading
        }
        loader={
          <div className="flex justify-center py-4" key="loader">
            <div className="text-gray-500">Chargement...</div>
          </div>
        }
      >
        <div className="w-full flex-wrap flex justify-center xl:justify-start items-start gap-x-6 gap-y-6 mt-4">
          {displayedShops.length > 0
            ? displayedShops.map((publisher, i) =>
                GreenIndexes.includes(i) && windowSize.width > 1024 ? (
                  <InlistShop key={`shop-${publisher.id}-${i}`} />
                ) : (
                  <PublisherCard
                    key={`shop-${publisher.id}-${i}`}
                    userDetails={publisher}
                    NeufPage={true}
                  />
                )
              )
            : loadingState.hasLoadedAll && !loadingState.isLoading
            ? renderEmptyState()
            : renderLoadingState()}
        </div>
      </InfiniteScroll>
    </div>
  );
};

export default Neuf;

Neuf.getLayout = function getLayout(page: JSX.Element) {
  return (
    <Layout>
      <Layout.Wide>{page}</Layout.Wide>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  try {
    const paginationParams = new PaginationParams();
    paginationParams.setLimit(LIMIT);
    paginationParams.setSkip(0);

    const matchParams = new MatchParams();
    matchParams.setPaginationparams(paginationParams);

    const searchShopRequest = new SearchUsersByTitleRequest();
    searchShopRequest.setMatchparams(matchParams);
    searchShopRequest.setSubcategoryid(NEUF_SUBCATEGORY_ID);

    const [fetchedShops, countShops] = await Promise.all([
      searchShopsByText('', searchShopRequest),
      countShopsByText(searchShopRequest),
    ]);

    const mappedShops = fetchedShops.map(mapUserResponseToIUserData);

    return {
      props: cleanObject({
        ...(await serverSideTranslations(locale || 'en')),
        shops: mappedShops,
        countShops: countShops.getCount(),
      }),
    };
  } catch (error) {
    console.error('Error in getServerSideProps:', error);

    return {
      props: cleanObject({
        ...(await serverSideTranslations(locale || 'en')),
        shops: [],
        countShops: 0,
      }),
    };
  }
};
